/**
 * La imagen que se comparte — dibujada a mano en un <canvas>.
 *
 * ── Por qué canvas y no html2canvas (que ya es dependencia del repo) ──────
 *
 * html2canvas saca una FOTO DE LA INTERFAZ. Para que no salgan los botones,
 * el scroll ni el fondo del modal habría que montar igual un nodo aparte,
 * fuera de pantalla, y maquetarlo entero: o sea, el mismo trabajo de layout,
 * pero además con estos riesgos propios de este repo:
 *
 *   · Las variables CSS del tema (`--cream`, `--gold`, `--ink`) viven en
 *     `.hc-page`. Un nodo montado fuera de ese árbol —o el modal, que sale
 *     por portal al body— las pierde. Es la trampa 4, y ya mordió acá.
 *   · html2canvas 1.4.1 no implementa `object-fit`, ni `gap` de flex en todos
 *     los casos, ni las máscaras de `radial-gradient` que usa el troquel del
 *     talón. Lo que se ve en pantalla y lo que sale en el PNG no coinciden, y
 *     esa diferencia no la detecta ni el build ni el lint (N5 otra vez).
 *   · La resolución sale atada al layout del DOM. WhatsApp recomprime: la
 *     imagen tiene que salir con el número grande y nítido a 1080 px de ancho,
 *     no al tamaño que le tocó al modal en la ventana de quien comparte.
 *
 * Dibujar a mano cuesta más código una sola vez y da una pieza DISEÑADA para
 * WhatsApp: proporción cercana al cuadrado, número enorme, márgenes propios,
 * y el mismo resultado en cualquier pantalla y en cualquier navegador.
 *
 * ── Qué queda quemado en píxeles ─────────────────────────────────────────
 * El motivo de que esto sea imagen y no texto es que un texto se edita antes
 * de reenviarlo. Entonces TODO lo que hace verificable el dato va adentro de
 * la imagen: lotería, turno, número de sorteo, fecha, los números, la
 * atribución a Lotería Jujeña, la marca INPROJUY y el dominio del sitio.
 *
 * Y en la jugada del simulador, lo mismo con el aviso: "JUGADA SIMULADA · NO
 * ES UNA APUESTA" va en una banda dorada arriba de todo, más una marca de agua
 * en diagonal sobre toda la pieza. El texto que acompaña se borra al
 * reenviar; la banda y la marca de agua, no.
 *
 * ⚠️ N1 · Acá no se inventa un dato. Todo lo que se dibuja llega por
 * parámetro desde el extracto real o desde la jugada que armó la persona.
 */

const PUB = process.env.PUBLIC_URL || '';

export const SITIO = 'https://inprojuy.gob.ar';
export const SITIO_CORTO = 'inprojuy.gob.ar';

const ANCHO = 1080;
const MARGEN = 72;
const CONTENIDO = ANCHO - MARGEN * 2; // 936

/* Los colores van escritos completos, sin una sola variable CSS: el canvas no
   hereda nada de `.hc-page` y este módulo se usa también desde el modal, que
   sale por portal al body. */
const C = {
  fondoA: '#123239',
  fondoB: '#07161A',
  crema: '#F7F5F0',
  oro: '#F4B740',
  oroClaro: '#FFD068',
  tinta: '#08191E',
  apagado: '#A9BAB7', // 7,3:1 sobre el fondo — AAA en texto chico
  linea: 'rgba(247,245,240,.14)',
  celda: 'rgba(247,245,240,.07)',
  celdaBorde: 'rgba(247,245,240,.18)',
};

const DISPLAY = '"Bricolage Grotesque", Georgia, serif';
const TEXTO = '"Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", sans-serif';
const MONO = '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

const FUENTES_A_CARGAR = [
  `800 54px ${DISPLAY}`,
  `700 30px ${TEXTO}`,
  `800 26px ${TEXTO}`,
  `700 140px ${MONO}`,
  `700 46px ${MONO}`,
];

/* ─────────────────────────────── Utilidades ─────────────────────────────── */

/** Nunca rechaza: si el logo no carga, la pieza se dibuja sin él. */
function cargarImagen(src) {
  return new Promise((resolver) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolver(img);
    img.onerror = () => resolver(null);
    img.src = src;
  });
}

/** Espera a las webfonts. Si fallan, el canvas cae a las de sistema y listo. */
async function asegurarFuentes() {
  try {
    if (!document.fonts || !document.fonts.load) return;
    await Promise.all(FUENTES_A_CARGAR.map((f) => document.fonts.load(f)));
  } catch (e) {
    /* sin webfonts la pieza se ve peor, pero se ve */
  }
}

function rectRedondo(ctx, x, y, an, al, r) {
  const rr = Math.min(r, an / 2, al / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + an, y, x + an, y + al, rr);
  ctx.arcTo(x + an, y + al, x, y + al, rr);
  ctx.arcTo(x, y + al, x, y, rr);
  ctx.arcTo(x, y, x + an, y, rr);
  ctx.closePath();
}

/**
 * Texto con tracking, dibujado letra por letra.
 * `ctx.letterSpacing` existe en Chrome pero no en Safari viejo, y estas
 * imágenes se generan en el teléfono de cualquiera.
 */
function textoEspaciado(ctx, txt, x, y, esp, alinear) {
  const letras = [...String(txt)];
  const total = letras.reduce((a, l) => a + ctx.measureText(l).width + esp, 0) - esp;
  let cx = x;
  if (alinear === 'derecha') cx = x - total;
  if (alinear === 'centro') cx = x - total / 2;
  const previo = ctx.textAlign;
  ctx.textAlign = 'left';
  letras.forEach((l) => {
    ctx.fillText(l, cx, y);
    cx += ctx.measureText(l).width + esp;
  });
  ctx.textAlign = previo;
  return total;
}

/** Parte un texto en líneas que entren en `maxAncho`. */
function envolver(ctx, txt, maxAncho) {
  const palabras = String(txt).split(/\s+/);
  const lineas = [];
  let actual = '';
  palabras.forEach((p) => {
    const prueba = actual ? `${actual} ${p}` : p;
    if (ctx.measureText(prueba).width > maxAncho && actual) {
      lineas.push(actual);
      actual = p;
    } else {
      actual = prueba;
    }
  });
  if (actual) lineas.push(actual);
  return lineas;
}

function parrafo(ctx, txt, x, y, maxAncho, interlinea) {
  const lineas = envolver(ctx, txt, maxAncho);
  lineas.forEach((l, i) => ctx.fillText(l, x, y + i * interlinea));
  return lineas.length * interlinea;
}

/** El número más grande que entra en `maxAncho` con la fuente pedida. */
function fuenteQueEntra(ctx, txt, px, maxAncho, armar) {
  let t = px;
  for (let i = 0; i < 24; i += 1) {
    ctx.font = armar(t);
    if (ctx.measureText(txt).width <= maxAncho) break;
    t -= Math.max(2, Math.round(t * 0.06));
  }
  return t;
}

/** Fondo de la pieza: el mismo degradado del modal de los 20 números. */
function fondo(ctx, alto) {
  const g = ctx.createLinearGradient(0, 0, ANCHO * 0.45, alto);
  g.addColorStop(0, C.fondoA);
  g.addColorStop(0.78, C.fondoB);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, ANCHO, alto);
}

/**
 * Pastilla crema con un logo adentro.
 *
 * La pastilla no es decoración: los cinco logos de las loterías son tinta
 * oscura sobre transparente y sobre este fondo desaparecerían. Es la misma
 * razón por la que existe `.te-logo` en la variante oscura del tablero.
 *
 * El logo va con "contain" calculado a mano —width/height al 100% y
 * object-fit no existen en canvas— porque es exactamente la trampa 2 del
 * repo: escalar por el ancho desborda y se come lo de abajo.
 */
function pastillaLogo(ctx, img, x, y, an, al, pad) {
  ctx.fillStyle = C.crema;
  rectRedondo(ctx, x, y, an, al, 18);
  ctx.fill();
  if (!img || !img.width || !img.height) return;
  const dispAn = an - pad * 2;
  const dispAl = al - pad * 2;
  const k = Math.min(dispAn / img.width, dispAl / img.height);
  const w = img.width * k;
  const h = img.height * k;
  ctx.drawImage(img, x + (an - w) / 2, y + (al - h) / 2, w, h);
}

/** Cabecera común: marca INPROJUY + rótulo de qué es esta imagen. */
function cabecera(ctx, logoInpro, rotulo) {
  const alto = 82;
  if (logoInpro && logoInpro.width) {
    const k = alto / logoInpro.height;
    ctx.drawImage(logoInpro, MARGEN, 46, logoInpro.width * k, alto);
  } else {
    ctx.fillStyle = C.crema;
    ctx.font = `800 40px ${DISPLAY}`;
    ctx.textBaseline = 'top';
    ctx.fillText('INPROJUY', MARGEN, 66);
  }

  ctx.fillStyle = C.oro;
  ctx.font = `800 24px ${TEXTO}`;
  ctx.textBaseline = 'top';
  textoEspaciado(ctx, rotulo, ANCHO - MARGEN, 74, 3.4, 'derecha');

  ctx.fillStyle = C.oro;
  ctx.fillRect(MARGEN, 150, CONTENIDO, 3);
  return 150 + 3;
}

/** Pie común: atribución + dominio. Devuelve el alto total de la pieza. */
function pie(ctx, y, atribucion) {
  let cy = y + 56;
  ctx.fillStyle = C.linea;
  ctx.fillRect(MARGEN, cy, CONTENIDO, 1);
  cy += 40;

  ctx.textBaseline = 'top';
  ctx.fillStyle = C.apagado;
  ctx.font = `500 26px ${TEXTO}`;
  cy += parrafo(ctx, atribucion, MARGEN, cy, CONTENIDO, 36);

  cy += 14;
  ctx.fillStyle = C.oro;
  ctx.font = `800 34px ${TEXTO}`;
  ctx.fillText(SITIO_CORTO, MARGEN, cy);
  cy += 44;

  return cy + MARGEN - 16;
}

/**
 * Dibuja en dos pasadas: la primera mide, la segunda pinta sobre un canvas
 * del alto exacto. El fondo necesita saber el alto final, así que no hay
 * forma de hacerlo en una sola.
 */
function componer(dibujar) {
  const medidor = document.createElement('canvas');
  medidor.width = ANCHO;
  medidor.height = 4000;
  const alto = Math.ceil(dibujar(medidor.getContext('2d')));

  const lienzo = document.createElement('canvas');
  lienzo.width = ANCHO;
  lienzo.height = alto;
  dibujar(lienzo.getContext('2d'));
  return lienzo;
}

function aArchivo(lienzo, nombre) {
  return new Promise((resolver) => {
    lienzo.toBlob((blob) => {
      if (!blob) {
        resolver(null);
        return;
      }
      resolver(new File([blob], nombre, { type: 'image/png' }));
    }, 'image/png');
  });
}

/** Nombre de archivo sin acentos ni espacios. */
export function nombreArchivo(...partes) {
  const base = partes
    .filter(Boolean)
    .join('-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `${base || 'inprojuy'}.png`;
}

/* ══════════════ Imagen del extracto de un juego de bolillero ══════════════

   La tercera pieza de este módulo. Las otras dos —extracto de quiniela y
   jugada simulada— dibujan UN número grande; acá hay varias modalidades, cada
   una con su fila de bolillas y su titular de premio, y el alto sale de
   cuántas haya. Por eso todo se mide antes de pintar (`componer` ya dibuja
   dos veces) y nada tiene posición fija.

   El vestido del juego entra igual que en pantalla: el logo, un filete del
   color de marca —que NO lleva texto encima— y la forma de la bolilla que
   usa el sitio del propio juego. La tinta que sí lleva texto es la corregida
   (`tinta`), no el color de marca crudo.

   ⚠️ Los colores van escritos completos, como todo este módulo: el canvas no
   hereda ninguna variable de `.hc-page`. */

/** Una bolilla, con la forma que le da su casa. Devuelve el ancho ocupado. */
function bolilla(ctx, valor, x, y, diam, opciones = {}) {
  const relleno = opciones.relleno || '#FFFFFF';
  const tinta = opciones.tinta || C.tinta;
  const r = diam / 2;

  ctx.save();
  ctx.fillStyle = relleno;
  if (opciones.ficha === 'cuadrado') {
    // Santa Fe dibuja casilleros cuadrados, no bolillas.
    rectRedondo(ctx, x, y, diam, diam, 6);
    ctx.fill();
  } else {
    ctx.beginPath();
    ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
    ctx.fill();
    if (opciones.ficha === 'anillo') {
      // Loto 5 Plus: bolilla blanca con contorno oscuro.
      ctx.strokeStyle = '#101010';
      ctx.lineWidth = Math.max(2, diam * 0.045);
      ctx.stroke();
    }
  }
  if (opciones.borde) {
    ctx.strokeStyle = opciones.borde;
    ctx.lineWidth = Math.max(2, diam * 0.05);
    if (opciones.ficha === 'cuadrado') {
      rectRedondo(ctx, x, y, diam, diam, 6);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(x + r, y + r, r, 0, Math.PI * 2);
      ctx.stroke();
    }
  }

  ctx.fillStyle = tinta;
  ctx.font = `700 ${Math.round(diam * 0.44)}px ${MONO}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(valor), x + r, y + r + 2);

  if (opciones.repetido) {
    // El número repetido del Premio Extra va tachado, como lo tacha la fuente.
    ctx.strokeStyle = tinta;
    ctx.lineWidth = Math.max(2, diam * 0.05);
    ctx.beginPath();
    ctx.moveTo(x + diam * 0.16, y + r);
    ctx.lineTo(x + diam * 0.84, y + r);
    ctx.stroke();
  }
  ctx.restore();
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  return diam;
}

/**
 * @param {object} d
 * @param {string}  d.juego        "Loto Plus"
 * @param {string}  d.logo         ruta del logo del juego
 * @param {string}  d.acento       color de marca — SOLO el filete, sin texto
 * @param {string}  d.tinta        la tinta legible del juego
 * @param {string}  d.ficha        'circulo' | 'anillo' | 'cuadrado'
 * @param {string}  d.contexto     "Sorteo Nº 3909 · 15/08/2026"
 * @param {string}  [d.pozo]       pozo estimado ya formateado
 * @param {string}  [d.proximo]    "Próximo sorteo: miércoles 19/08"
 * @param {Array}   d.bloques      [{ nombre, numeros, repetidos, titular,
 *                                    destacado:{valor,rotulo} }]
 * @param {object}  [d.plus]       { rotulo, valor, color }
 * @param {string}  d.atribucion   quién publicó el extracto
 */
export async function imagenSorteo(d) {
  await asegurarFuentes();
  const [logoInpro, logoJuego] = await Promise.all([
    cargarImagen(`${PUB}/img/logo_inpro_white.png`),
    cargarImagen(d.logo),
  ]);

  const lienzo = componer((ctx) => {
    fondo(ctx, ctx.canvas.height);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    let y = cabecera(ctx, logoInpro, 'EXTRACTO OFICIAL');

    y += 42;
    pastillaLogo(ctx, logoJuego, MARGEN, y, 380, 128, 15);
    y += 128 + 26;

    ctx.fillStyle = C.crema;
    const tam = fuenteQueEntra(ctx, d.juego, 54, CONTENIDO, (t) => `800 ${t}px ${DISPLAY}`);
    ctx.fillText(d.juego, MARGEN, y);
    y += tam + 12;

    ctx.fillStyle = C.apagado;
    ctx.font = `600 29px ${TEXTO}`;
    y += parrafo(ctx, d.contexto, MARGEN, y, CONTENIDO, 38);

    // El filete del color del juego. No lleva texto encima, así que el color
    // de marca crudo puede vivir acá sin pasar por el mínimo de contraste.
    y += 24;
    ctx.fillStyle = d.acento || C.oro;
    ctx.fillRect(MARGEN, y, 168, 6);
    y += 6 + 30;

    if (d.pozo) {
      ctx.fillStyle = C.apagado;
      ctx.font = `700 22px ${TEXTO}`;
      textoEspaciado(ctx, 'POZO ESTIMADO', MARGEN, y, 3.2);
      y += 34;
      ctx.fillStyle = d.tinta || C.oro;
      const tp = fuenteQueEntra(ctx, d.pozo, 62, CONTENIDO, (t) => `800 ${t}px ${DISPLAY}`);
      ctx.fillText(d.pozo, MARGEN, y);
      y += tp + 18;
    }
    if (d.proximo) {
      ctx.fillStyle = C.crema;
      ctx.font = `600 27px ${TEXTO}`;
      y += parrafo(ctx, d.proximo, MARGEN, y, CONTENIDO, 38);
      y += 8;
    }

    /* Una modalidad por bloque: nombre, la fila de bolillas y el titular del
       premio. Nunca una tabla: en la miniatura de un chat una tabla de cuatro
       columnas no se lee, y si no se lee no se abre. */
    (d.bloques || []).forEach((b) => {
      y += 34;
      ctx.fillStyle = d.tinta || C.oro;
      ctx.font = `800 26px ${TEXTO}`;
      textoEspaciado(ctx, String(b.nombre || '').toUpperCase(), MARGEN, y, 2.6);
      y += 40;

      const porFila = b.numeros.length > 6 ? 9 : Math.max(1, b.numeros.length);
      const hueco = 16;
      const diam = Math.min(
        112,
        Math.floor((CONTENIDO - hueco * (porFila - 1)) / porFila),
      );
      b.numeros.forEach((n, i) => {
        const col = i % porFila;
        const fila = Math.floor(i / porFila);
        bolilla(ctx, n, MARGEN + col * (diam + hueco), y + fila * (diam + hueco), diam, {
          ficha: d.ficha,
          repetido: !!(b.repetidos && b.repetidos[i]),
        });
      });
      const filas = Math.ceil(b.numeros.length / porFila);
      y += filas * diam + (filas - 1) * hueco;

      if (b.destacado) {
        y += 22;
        const dd = Math.min(96, diam);
        bolilla(ctx, b.destacado.valor, MARGEN, y, dd, {
          ficha: d.ficha,
          relleno: b.destacado.color || C.oro,
        });
        ctx.fillStyle = C.crema;
        ctx.font = `800 28px ${TEXTO}`;
        ctx.fillText(b.destacado.rotulo, MARGEN + dd + 22, y + dd / 2 - 18);
        y += dd;
      }

      if (b.titular) {
        y += 20;
        ctx.fillStyle = C.crema;
        ctx.font = `600 27px ${TEXTO}`;
        y += parrafo(ctx, b.titular, MARGEN, y, CONTENIDO, 38);
      }
    });

    if (d.plus) {
      y += 44;
      const diam = 118;
      bolilla(ctx, d.plus.valor, MARGEN, y, diam, {
        ficha: 'circulo',
        relleno: d.plus.color || C.oro,
      });
      ctx.fillStyle = C.crema;
      ctx.font = `800 34px ${TEXTO}`;
      ctx.fillText(d.plus.rotulo, MARGEN + diam + 26, y + diam / 2 - 22);
      y += diam;
    }

    return pie(ctx, y, d.atribucion);
  });

  return aArchivo(
    lienzo,
    nombreArchivo('extracto', d.juego, d.contexto && d.contexto.replace(/[^\d]/g, '-')),
  );
}

/* ═════════════════════════ Imagen de un extracto ═════════════════════════ */

/**
 * @param {object} d
 * @param {string} d.loteria      nombre completo — "Tómbola Jujeña"
 * @param {string} d.logo         ruta del logo de esa lotería
 * @param {string} d.turno        "Nocturna"
 * @param {number|string} d.sorteoNro
 * @param {string} d.fecha        "19/08/2026", tal como llega del extracto
 * @param {string[]} d.numeros    los 20, en orden de salida
 * @param {boolean} d.todos       true = la grilla de 20; false = solo la cabeza
 */
export async function imagenExtracto(d) {
  await asegurarFuentes();
  const [logoInpro, logoLot] = await Promise.all([
    cargarImagen(`${PUB}/img/logo_inpro_white.png`),
    cargarImagen(d.logo),
  ]);

  const cabeza = String(d.numeros[0]);
  const contexto = [
    d.turno,
    d.sorteoNro ? `Sorteo Nº ${d.sorteoNro}` : null,
    d.fecha,
  ].filter(Boolean).join(' · ');

  const lienzo = componer((ctx) => {
    fondo(ctx, ctx.canvas.height);
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    let y = cabecera(ctx, logoInpro, 'EXTRACTO OFICIAL');

    /* Identificación: pastilla con el logo, nombre de la lotería, y el
       turno + sorteo + fecha. Sin esta línea el número no es verificable. */
    y += 42;
    pastillaLogo(ctx, logoLot, MARGEN, y, 400, 132, 15);
    y += 132 + 30;

    ctx.fillStyle = C.crema;
    const tamNombre = fuenteQueEntra(ctx, d.loteria, 56, CONTENIDO, (t) => `800 ${t}px ${DISPLAY}`);
    ctx.fillText(d.loteria, MARGEN, y);
    y += tamNombre + 14;

    ctx.fillStyle = C.apagado;
    ctx.font = `600 30px ${TEXTO}`;
    y += parrafo(ctx, contexto, MARGEN, y, CONTENIDO, 40);

    /* La cabeza: es el número que la gente busca, y en la imagen es lo único
       que tiene que leerse desde la miniatura del chat. */
    y += 44;
    ctx.fillStyle = C.oro;
    ctx.font = `800 25px ${TEXTO}`;
    textoEspaciado(ctx, 'CABEZA · 1ER PREMIO', MARGEN, y, 4);
    y += 44;

    const altoPlaca = 208;
    const g = ctx.createLinearGradient(0, y, 0, y + altoPlaca);
    g.addColorStop(0, C.oroClaro);
    g.addColorStop(1, C.oro);
    ctx.fillStyle = g;
    rectRedondo(ctx, MARGEN, y, CONTENIDO, altoPlaca, 26);
    ctx.fill();

    ctx.fillStyle = C.tinta;
    fuenteQueEntra(ctx, cabeza, 148, CONTENIDO - 80, (t) => `700 ${t}px ${MONO}`);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(cabeza, ANCHO / 2, y + altoPlaca / 2 + 4);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    y += altoPlaca;

    /* Los 20, en la misma grilla de 5 columnas que el modal del sitio: quien
       recibe la imagen y entra al sitio encuentra el mismo objeto. */
    if (d.todos && d.numeros.length > 1) {
      y += 52;
      ctx.fillStyle = C.apagado;
      ctx.font = `700 23px ${TEXTO}`;
      textoEspaciado(ctx, 'LOS 20 NÚMEROS, EN ORDEN DE SALIDA', MARGEN, y, 3.2);
      y += 42;

      const cols = 5;
      const hueco = 14;
      const anCelda = (CONTENIDO - hueco * (cols - 1)) / cols;
      const alCelda = 100;

      d.numeros.forEach((n, i) => {
        const cx = MARGEN + (i % cols) * (anCelda + hueco);
        const cy = y + Math.floor(i / cols) * (alCelda + hueco);

        if (i === 0) {
          ctx.fillStyle = C.oro;
          rectRedondo(ctx, cx, cy, anCelda, alCelda, 16);
          ctx.fill();
        } else {
          ctx.fillStyle = C.celda;
          rectRedondo(ctx, cx, cy, anCelda, alCelda, 16);
          ctx.fill();
          ctx.strokeStyle = C.celdaBorde;
          ctx.lineWidth = 1;
          rectRedondo(ctx, cx + 0.5, cy + 0.5, anCelda - 1, alCelda - 1, 16);
          ctx.stroke();
        }

        ctx.fillStyle = i === 0 ? 'rgba(8,25,30,.62)' : C.apagado;
        ctx.font = `700 19px ${TEXTO}`;
        ctx.fillText(String(i + 1), cx + 12, cy + 10);

        ctx.fillStyle = i === 0 ? C.tinta : C.crema;
        ctx.font = `700 40px ${MONO}`;
        ctx.textAlign = 'center';
        ctx.fillText(String(n), cx + anCelda / 2, cy + 40);
        ctx.textAlign = 'left';
      });

      const filas = Math.ceil(d.numeros.length / cols);
      y += filas * alCelda + (filas - 1) * hueco;
    }

    return pie(ctx, y, 'Extracto oficial publicado por Lotería Jujeña. Verificalo en');
  });

  return aArchivo(
    lienzo,
    nombreArchivo(
      'extracto', d.loteria, d.turno,
      d.fecha && d.fecha.replace(/\//g, '-'),
      d.todos ? null : 'cabeza',
    ),
  );
}

/* ═════════════════════ Imagen de una jugada simulada ═════════════════════ */

/**
 * Marca de agua en diagonal. Es la defensa contra el recorte: alguien puede
 * cortar la banda dorada de arriba, pero no puede sacar esto sin que se note.
 * Va DEBAJO del contenido y a baja opacidad para no tocarle el contraste a
 * ningún texto: al 6,5% el fondo pasa de #07161A a ~#1A2528, y el texto
 * apagado #A9BAB7 encima sigue dando 7,9:1 — AAA, que es el piso de este
 * sitio también en texto chico. Al 10% ya bajaba de 7.
 */
function marcaDeAgua(ctx, alto, texto) {
  ctx.save();
  ctx.globalAlpha = 0.065;
  ctx.fillStyle = C.crema;
  ctx.font = `800 62px ${TEXTO}`;
  ctx.textBaseline = 'middle';
  ctx.translate(ANCHO / 2, alto / 2);
  ctx.rotate(-Math.PI / 6);
  const paso = 190;
  const largo = Math.hypot(ANCHO, alto);
  for (let fy = -largo / 2; fy < largo / 2; fy += paso) {
    let fx = -largo / 2;
    while (fx < largo / 2) {
      ctx.fillText(texto, fx, fy);
      fx += ctx.measureText(texto).width + 90;
    }
  }
  ctx.restore();
}

/**
 * @param {object} d
 * @param {string} d.juego        "Loto Plus"
 * @param {string} d.logo         logo del juego
 * @param {string} d.como         cómo se juega, tal cual sale de mecanicasJuegos
 * @param {'bolillero'|'quiniela'} d.familia
 * @param {string[]} d.bolillas   los números elegidos, ya formateados
 * @param {string} d.color        relleno de la bolilla (juego.bola)
 * @param {{valor:string,rotulo:string,color:string}} [d.extra]
 * @param {{digitos:string[],posicion:string,cifras:string}} [d.boleta]
 * @param {string} [d.sueno]
 */
export async function imagenJugada(d) {
  await asegurarFuentes();
  const [logoInpro, logoJuego] = await Promise.all([
    cargarImagen(`${PUB}/img/logo_inpro_white.png`),
    cargarImagen(d.logo),
  ]);

  const lienzo = componer((ctx) => {
    fondo(ctx, ctx.canvas.height);
    marcaDeAgua(ctx, ctx.canvas.height, 'SIMULACIÓN');
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    let y = cabecera(ctx, logoInpro, 'SIMULADOR');

    /* La banda. Es lo primero que se lee después de la marca, y es lo que
       impide que esto se reenvíe como si fuera una apuesta hecha. */
    y += 40;
    const altoBanda = 132;
    ctx.fillStyle = C.oro;
    rectRedondo(ctx, MARGEN, y, CONTENIDO, altoBanda, 22);
    ctx.fill();

    ctx.fillStyle = C.tinta;
    ctx.font = `800 46px ${TEXTO}`;
    textoEspaciado(ctx, 'JUGADA SIMULADA', MARGEN + 32, y + 24, 1.2);
    ctx.font = `600 28px ${TEXTO}`;
    ctx.fillText('No es una apuesta. No es un ticket.', MARGEN + 32, y + 80);
    y += altoBanda + 52;

    pastillaLogo(ctx, logoJuego, MARGEN, y, 356, 124, 14);
    y += 124 + 26;

    ctx.fillStyle = C.crema;
    const tamJuego = fuenteQueEntra(ctx, d.juego, 52, CONTENIDO, (t) => `800 ${t}px ${DISPLAY}`);
    ctx.fillText(d.juego, MARGEN, y);
    y += tamJuego + 16;

    ctx.fillStyle = C.apagado;
    ctx.font = `500 27px ${TEXTO}`;
    y += parrafo(ctx, d.como, MARGEN, y, CONTENIDO, 38);

    y += 48;

    if (d.familia === 'bolillero') {
      const conExtra = !!d.extra;
      const n = d.bolillas.length + (conExtra ? 1 : 0);
      const anchoMas = conExtra ? 46 : 0;
      const hueco = 18;
      const diam = Math.min(
        132,
        Math.floor((CONTENIDO - anchoMas - hueco * (n - 1)) / n),
      );
      let bx = MARGEN;
      const by = y;

      d.bolillas.forEach((v) => {
        ctx.fillStyle = d.color;
        ctx.beginPath();
        ctx.arc(bx + diam / 2, by + diam / 2, diam / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = C.tinta;
        ctx.font = `700 ${Math.round(diam * 0.42)}px ${MONO}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(v, bx + diam / 2, by + diam / 2 + 2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        bx += diam + hueco;
      });

      if (conExtra) {
        ctx.fillStyle = C.apagado;
        ctx.font = `400 46px ${DISPLAY}`;
        ctx.textBaseline = 'middle';
        ctx.fillText('+', bx - 4, by + diam / 2);
        ctx.textBaseline = 'top';
        bx += anchoMas;

        ctx.fillStyle = d.extra.color;
        ctx.beginPath();
        ctx.arc(bx + diam / 2, by + diam / 2, diam / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(247,245,240,.34)';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.fillStyle = C.tinta;
        ctx.font = `700 ${Math.round(diam * 0.42)}px ${MONO}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(d.extra.valor, bx + diam / 2, by + diam / 2 + 2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        ctx.fillStyle = C.apagado;
        ctx.font = `700 21px ${TEXTO}`;
        ctx.textAlign = 'center';
        ctx.fillText(d.extra.rotulo, bx + diam / 2, by + diam + 14);
        ctx.textAlign = 'left';
      }

      y += diam + (conExtra ? 46 : 0);
    } else {
      const dig = d.boleta.digitos;
      const hueco = 16;
      const anD = Math.min(132, Math.floor((CONTENIDO * 0.62 - hueco * (dig.length - 1)) / dig.length));
      const alD = Math.round(anD * 1.24);
      let bx = MARGEN;

      dig.forEach((v) => {
        ctx.fillStyle = C.celda;
        rectRedondo(ctx, bx, y, anD, alD, 18);
        ctx.fill();
        ctx.strokeStyle = C.celdaBorde;
        ctx.lineWidth = 2;
        rectRedondo(ctx, bx + 1, y + 1, anD - 2, alD - 2, 18);
        ctx.stroke();
        ctx.fillStyle = C.crema;
        ctx.font = `700 ${Math.round(anD * 0.6)}px ${MONO}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(v, bx + anD / 2, y + alD / 2 + 2);
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        bx += anD + hueco;
      });

      ctx.fillStyle = C.apagado;
      ctx.font = `600 28px ${TEXTO}`;
      ctx.fillText('a la', bx + 18, y + alD / 2 - 42);
      ctx.fillStyle = d.color;
      ctx.font = `800 40px ${DISPLAY}`;
      ctx.fillText(d.boleta.posicion, bx + 18, y + alD / 2 - 6);
      ctx.fillStyle = C.apagado;
      ctx.font = `700 21px ${TEXTO}`;
      textoEspaciado(ctx, d.boleta.cifras.toUpperCase(), bx + 18, y + alD / 2 + 44, 2.6);

      y += alD;

      if (d.sueno) {
        y += 30;
        ctx.fillStyle = C.apagado;
        ctx.font = `500 27px ${TEXTO}`;
        y += parrafo(ctx, d.sueno, MARGEN, y, CONTENIDO, 38);
      }
    }

    /* El aviso largo, también en píxeles. Sin promesa de premio, de monto ni
       de participación en ningún sorteo: INPROJUY no vende online. */
    y += 46;
    ctx.fillStyle = C.crema;
    ctx.font = `600 27px ${TEXTO}`;
    y += parrafo(
      ctx,
      'Se armó en el simulador de INPROJUY, que sirve para practicar cómo se juega.',
      MARGEN, y, CONTENIDO, 38,
    );
    y += 10;
    ctx.fillStyle = C.apagado;
    ctx.font = `500 27px ${TEXTO}`;
    y += parrafo(
      ctx,
      'No participa de ningún sorteo y no paga premio. Para jugar de verdad hay que ir a una agencia oficial.',
      MARGEN, y, CONTENIDO, 38,
    );

    return pie(ctx, y, 'Simulador educativo de INPROJUY. Probalo en');
  });

  return aArchivo(lienzo, nombreArchivo('jugada-simulada', d.juego));
}
